import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Calendar, Gift, RefreshCw, Send, ChevronLeft, MoreVertical, Phone, Video } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';

const SMSCampaignsSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-6 lg:p-12 relative overflow-hidden font-sans">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-indigo-100/40 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />

        <div className="flex flex-row items-center gap-8 lg:gap-16 max-w-6xl mx-auto z-10 w-full">
            
            {/* Left Content */}
            <div className="flex-1 space-y-8 lg:space-y-10 min-w-0">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 text-sm font-bold border border-blue-100 shadow-sm">
                    <MessageSquare className="w-4 h-4" />
                    <span>SMS Campaigns</span>
                </div>

                <div className="space-y-4">
                    <h2 className="text-3xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight">
                        Engage Patients <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Where They Are.</span>
                    </h2>
                    <p className="text-lg lg:text-xl text-slate-500 leading-relaxed max-w-xl font-medium">
                        98% of text messages are read within 3 minutes. Use our powerful defaults or build custom campaigns to keep your schedule full.
                    </p>
                </div>

                <div className="space-y-3 lg:space-y-4">
                    {[
                        { icon: Calendar, color: 'blue', title: 'Appointment Reminders', desc: 'Reduce no-shows automatically.' },
                        { icon: Gift, color: 'indigo', title: 'Birthday Wishes', desc: 'Make patients feel special.' },
                        { icon: RefreshCw, color: 'cyan', title: 'Patient Reactivation', desc: 'Bring dormant patients back.' }
                    ].map((item, i) => (
                        <div key={i} className="flex items-center gap-4 lg:gap-5 p-3 lg:p-4 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-${item.color}-50 flex items-center justify-center text-${item.color}-600 shrink-0`}>
                                <item.icon className="w-5 h-5 lg:w-6 lg:h-6" />
                            </div>
                            <div className="min-w-0">
                                <h4 className="font-bold text-slate-900 text-base lg:text-lg truncate">{item.title}</h4>
                                <p className="text-slate-500 text-sm font-medium truncate">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Visual - Modern Phone Interface */}
            <motion.div 
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="flex-1 w-full max-w-[340px] lg:max-w-[380px] xl:max-w-[420px] shrink-0"
            >
                <div className="relative aspect-[9/18] bg-slate-900 rounded-[3rem] shadow-2xl p-3 ring-8 ring-slate-800 border-4 border-slate-700">
                     {/* Dynamic Island / Notch */}
                     <div className="absolute top-6 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-20 flex items-center justify-center gap-2">
                         <div className="w-2 h-2 rounded-full bg-slate-800" />
                         <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                     </div>

                     {/* Screen Content */}
                     <div className="h-full bg-white rounded-[2.5rem] overflow-hidden flex flex-col relative">
                        
                        {/* Status Bar */}
                        <div className="h-14 bg-white/80 backdrop-blur-md sticky top-0 z-10 flex items-center justify-between px-6 pt-2">
                            <span className="text-xs font-semibold text-slate-900 ml-2">9:41</span>
                            <div className="flex gap-1.5 mr-2">
                                <div className="w-4 h-2.5 bg-slate-900 rounded-[2px]" />
                                <div className="w-0.5 h-1 bg-slate-900/30" />
                            </div>
                        </div>

                        {/* App Header */}
                        <div className="px-4 pb-4 border-b border-slate-100 flex items-center justify-between bg-white/80 backdrop-blur-md sticky top-14 z-10">
                            <div className="flex items-center gap-1 text-blue-600">
                                <ChevronLeft className="w-6 h-6" />
                                <span className="text-lg">Back</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <Avatar className="w-10 h-10 mb-1 border border-slate-100">
                                    <AvatarImage src="/assets/logo-icon.png" />
                                    <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">AD</AvatarFallback>
                                </Avatar>
                                <span className="text-xs font-bold text-slate-900">Asha Dental</span>
                            </div>
                            <div className="flex gap-4 text-blue-600">
                                <Video className="w-6 h-6 opacity-20" />
                                <Phone className="w-6 h-6 opacity-20" />
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 p-4 space-y-6 overflow-y-auto bg-slate-50">
                            <div className="text-center">
                                <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Today 9:41 AM</span>
                            </div>

                            <motion.div 
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ delay: 0.4 }}
                                className="flex flex-col items-start max-w-[85%]"
                            >
                                <div className="bg-slate-200 text-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm text-[15px] leading-relaxed shadow-sm">
                                    Hi Sarah! It's your birthday month! 🎂 Come in for a cleaning this month and get a free whitening kit on us.
                                </div>
                            </motion.div>
                            
                            <motion.div 
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ delay: 1.2 }}
                                className="flex flex-col items-end self-end max-w-[85%] ml-auto"
                            >
                                <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tr-sm text-[15px] leading-relaxed shadow-md">
                                    That sounds great! Can I book for next Tuesday?
                                </div>
                                <span className="text-[10px] text-slate-400 mt-1 font-medium">Read 9:45 AM</span>
                            </motion.div>

                            <motion.div 
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ delay: 2.2 }}
                                className="flex flex-col items-start max-w-[85%]"
                            >
                                <div className="bg-slate-200 text-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm text-[15px] leading-relaxed shadow-sm">
                                    Absolutely! Click here to grab your spot: <span className="text-blue-600 underline decoration-blue-600/30">asha.dental/book/sarah-j</span>
                                </div>
                            </motion.div>
                             
                            {/* Typing Indicator */}
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 3.5, duration: 0.5 }}
                                className="flex items-center gap-1 ml-1"
                            >
                                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </motion.div>
                        </div>

                         {/* Input Area */}
                         <div className="p-3 bg-white border-t border-slate-100 flex items-center gap-3 pb-8">
                             <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                <span className="text-lg leading-none mb-1">+</span>
                             </div>
                             <div className="flex-1 h-9 bg-slate-100 rounded-full border border-slate-200 px-4 flex items-center text-sm text-slate-400">
                                iMessage
                             </div>
                             <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-sm">
                                <Send className="w-4 h-4 ml-0.5" />
                             </div>
                         </div>
                     </div>
                </div>
            </motion.div>
        </div>
    </div>
  );
};

export default SMSCampaignsSlide;
